import type { ContractorProfile, ContractorTimeEntry } from '../types';

const CONTRACTOR_TZ = 'America/Chicago';

/** YYYY-MM-DD in company timezone (matches server workDateKey). */
export function contractorWorkDateKey(workDate: Date): string {
  return workDate.toLocaleDateString('en-CA', { timeZone: CONTRACTOR_TZ });
}

export type BillableDaysByProject = Map<string, { days: number; minutes: number }>;

/** Distinct calendar work dates per project (one project per day assumed). */
export function countBillableDaysByProject(entries: ContractorTimeEntry[]): BillableDaysByProject {
  const datesByProject = new Map<string, Set<string>>();
  const minutesByProject = new Map<string, number>();

  for (const entry of entries) {
    if (!entry.projectId || entry.minutes <= 0) continue;
    const dateKey = contractorWorkDateKey(entry.workDate);
    const dates = datesByProject.get(entry.projectId) || new Set<string>();
    dates.add(dateKey);
    datesByProject.set(entry.projectId, dates);
    minutesByProject.set(entry.projectId, (minutesByProject.get(entry.projectId) || 0) + entry.minutes);
  }

  const out: BillableDaysByProject = new Map();
  datesByProject.forEach((dates, projectId) => {
    out.set(projectId, {
      days: dates.size,
      minutes: minutesByProject.get(projectId) || 0,
    });
  });
  return out;
}

export function totalBillableDays(entries: ContractorTimeEntry[]): number {
  let total = 0;
  countBillableDaysByProject(entries).forEach(({ days }) => {
    total += days;
  });
  return total;
}

/** Stored on day-log entries; invoicing counts distinct project dates, not minutes. */
export const CONTRACTOR_DAY_ENTRY_MINUTES = 1;

/** Inclusive YYYY-MM-DD range (local calendar dates from date inputs). */
export function inclusiveWorkDateKeys(fromKey: string, toKey: string): string[] {
  const a = String(fromKey || '').trim();
  const b = String(toKey || '').trim();
  if (!a || !b) return [];
  const start = a <= b ? a : b;
  const end = a <= b ? b : a;
  const out: string[] = [];
  const cursor = new Date(`${start}T12:00:00`);
  const last = new Date(`${end}T12:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) return [];
  while (cursor <= last) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const day = String(cursor.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${day}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

export function draftDayLogExists(
  entries: Pick<ContractorTimeEntry, 'projectId' | 'workDate' | 'status'>[],
  projectId: string,
  workDate: Date,
): boolean {
  const dateKey = contractorWorkDateKey(workDate);
  return entries.some(
    (e) =>
      e.status === 'draft'
      && e.projectId === projectId
      && contractorWorkDateKey(e.workDate) === dateKey,
  );
}

export function formatContractorEntryQuantity(minutes: number, logsByDay: boolean): string {
  if (logsByDay) return minutes > 0 ? '1 day' : '—';
  return `${(minutes / 60).toFixed(2)}h`;
}

export function contractorRequiresDayRateInvoicing(profile: ContractorProfile | null | undefined): boolean {
  return profile?.invoiceBillingMode === 'day_rate_required';
}

const INSTALL_LABOR_HOURS_PER_DAY = 8;

/** Day rate for proposal install labor lines (1099 pool). */
export function contractorInstallLaborDayRate(
  profile: ContractorProfile | null | undefined,
  lastUsedDayRate?: number | null,
): number | null {
  const fromProfileDay = profile?.defaultDayRate;
  if (typeof fromProfileDay === 'number' && fromProfileDay > 0) {
    return Math.round(fromProfileDay * 100) / 100;
  }
  if (typeof lastUsedDayRate === 'number' && lastUsedDayRate > 0) {
    return Math.round(lastUsedDayRate * 100) / 100;
  }
  if (contractorRequiresDayRateInvoicing(profile)) {
    return null;
  }
  const hourly = profile?.defaultHourlyRate;
  if (typeof hourly === 'number' && hourly > 0) {
    return Math.round(hourly * INSTALL_LABOR_HOURS_PER_DAY * 100) / 100;
  }
  return null;
}

export function defaultSubmitBillingFromProfile(profile: ContractorProfile | null | undefined): {
  billingUnit: 'hour' | 'day';
  rate: number;
} {
  if (contractorRequiresDayRateInvoicing(profile)) {
    return {
      billingUnit: 'day',
      rate: profile?.defaultDayRate && profile.defaultDayRate > 0 ? profile.defaultDayRate : 0,
    };
  }
  return {
    billingUnit: 'hour',
    rate: profile?.defaultHourlyRate && profile.defaultHourlyRate > 0 ? profile.defaultHourlyRate : 0,
  };
}

export function profileRateConfiguredForSubmit(profile: ContractorProfile | null | undefined): boolean {
  const { rate } = defaultSubmitBillingFromProfile(profile);
  return rate > 0;
}
